<template>
  <div class="zg-upload">
    <div v-if="title" class="c-6 m-b10" :class="{ 'filed-required': required }">
      {{ title }}
    </div>
    <el-upload
      class="upload-box"
      action=""
      drag
      :multiple="!singleFile"
      :accept="getAccept"
      :limit="50"
      :on-exceed="handleExceed"
      :show-file-list="false"
      :on-change="onChange"
      :auto-upload="false"
      :disabled="disabled"
    >
      <!-- 多张图片时，排列会换行，因此留了底部距离。所以容器此时 padding 相应减小 -->
      <div>
        <div v-if="showTip" class="file-dragger">
          <img
            v-show="parsedFileList.length === 0 && !isLoading"
            class="icon-upload m-b20"
            src="~assets/icons/upload.svg"
          />
          <div class="el-upload__text">
            <img
              v-show="parsedFileList.length > 0 || isLoading"
              class="icon-upload--mini"
              src="~assets/icons/upload.svg"
            />
            将文件拖拽到此处，或
            <em v-if="parsedFileList.length > 0 && singleFile">点此上传</em>
            <em v-else>点击上传</em>
          </div>
        </div>
        <div v-if="showLimit && showTip" class="tip f-12 c-c m-t15">
          <span class="m-r15">格式：{{ getAcceptDesc }}</span>
          <br v-show="parsedFileList.length === 0" />
          大小：小于{{ maxSize | fileSize }}
        </div>
        <!-- 已上传文件列表 -->
        <div
          v-show="parsedFileList.length || uploadingQueue.length || waitingQueue.length"
          class="file-uploaded p-lr50 m-t20"
          @click.stop
        >
          <el-row class="file-list" type="flex" justify="center" :gutter="20">
            <template v-if="parsedFileList.length">
              <el-col
                v-for="file in parsedFileList"
                :key="file.uid"
                :span="6"
                class="file--multiple relative-view"
              >
                <template v-if="file.type === 'image'">
                  <el-image
                    fit="cover"
                    :src="file.previewAndDownloadUrl"
                    class="img-border"
                  ></el-image>
                </template>
                <template v-else-if="file.type === 'video'">
                  <img src="@/assets/icons/file-video.svg" />
                </template>
                <div v-else class="file--others relative-view">
                  <span class="suffix c-f">{{ file.suffix }}</span>
                  <img src="@/assets/icons/file-others.svg" />
                </div>
                <div class="m-t10 two-line p-lr10">{{ file.name }}</div>
                <div class="mask">
                  <img
                    v-if="!disabled"
                    class="icon--del"
                    src="@/assets/icons/delete.svg"
                    @click="onClickDel(file)"
                  />
                  <div class="flex action-bar p-lr5">
                    <div class="flex-full" @click="onClickDownload(file.previewAndDownloadUrl)">
                      <el-image :src="require('@/assets/icons/download.svg')"></el-image>
                    </div>
                    <div v-if="file.type === 'image'" class="flex-full">
                      <el-image
                        :src="require('@/assets/icons/zoom_in.svg')"
                        @click="preview(file.previewAndDownloadUrl)"
                      ></el-image>
                    </div>
                  </div>
                </div>
              </el-col>
            </template>
            <!-- 上传进度条 -->
            <el-col v-for="file in uploadingQueue" :key="file.uid" :span="6" class="file--multiple">
              <div class="file--others relative-view">
                <span class="suffix c-f">{{
                  file.progress !== 100 ? file.progress + '%' : '处理中'
                }}</span>
                <img src="@/assets/icons/file-others.svg" />
              </div>
              <div class="m-t10 two-line p-lr10">{{ file.name }}</div>
            </el-col>
            <el-col v-for="file in waitingQueue" :key="file.uid" :span="6" class="file--multiple">
              <div class="file--others relative-view">
                <span class="suffix c-f">等待中</span>
                <img src="@/assets/icons/file-others.svg" />
              </div>
              <div class="m-t10 two-line p-lr10">{{ file.name }}</div>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-upload>
  </div>
</template>

<script>
import { getInfoFromUrl } from 'common/utils/file.js'
import { uploadFile } from '@/common/services/upload'
import { makeImage } from '@/common/filters'
import preview from '@/common/utils/preview'

// 上传文件类型限制码表
const FILE_TYPE_LIMIT = {
  any: {
    accept: '',
    desc: 'JPG, PNG, GIF, DOCX, PDF, XLS 等'
  },
  image: {
    accept: 'image/gif,image/png,image/jpeg,image/jpg,image/bmp',
    desc: 'JPG, PNG, GIF 等'
  },
  excel: {
    accept: '.xls,.xlsx',
    desc: 'xls, xlsx 等'
  },
  // 发票附件
  annex: {
    accept: '.jpg,.png,.bmp,.pdf',
    desc: 'jpg, png, bmp, pdf'
  }
}

const maxQueueCount = 3

export default {
  name: 'ZgUpload',
  components: {},
  props: {
    title: {
      type: String,
      default: ''
    },
    showLimit: {
      type: Boolean,
      default: true
    },
    showTip: {
      type: Boolean,
      default: true
    },
    singleFile: {
      type: Boolean,
      default: false
    },
    fileList: {
      type: Array,
      default() {
        return []
      }
    },
    required: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1000 // 10M
    },
    disabled: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: 'any'
    },
    extraParam: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      uploadingQueue: [], // 上传队列
      waitingQueue: [] // 等待队列
    }
  },
  beforeDestroy() {
    preview.previewDestroy()
  },
  computed: {
    isLoading() {
      return this.uploadingQueue.length || this.waitingQueue.length
    },
    parsedFileList() {
      return this.fileList
        .filter(file => file.fk) // file.fk 不为空
        .map(file => {
          const info = getInfoFromUrl(file.fk)
          return {
            ...file,
            ...info,
            previewAndDownloadUrl: makeImage(file.fk, {}, false)
          }
        })
    },
    imagePreviewList() {
      return this.parsedFileList
        .filter(file => file.type === 'image')
        .map(file => file.previewAndDownloadUrl)
    },
    getContainerClass() {
      const { singleFile, parsedFileList } = this
      if (singleFile && parsedFileList.length === 0) {
        return 'p-t60 p-b60'
      } else {
        if (parsedFileList.length > 0) {
          return 'p-t30 p-b5'
        } else {
          return 'p-t30 p-b30'
        }
      }
    },
    getAccept() {
      return (FILE_TYPE_LIMIT[this.accept] || {}).accept
    },
    getAcceptDesc() {
      return (FILE_TYPE_LIMIT[this.accept] || {}).desc
    }
  },
  methods: {
    preview(url) {
      const initialViewIndex = this.imagePreviewList.findIndex(u => u === url)
      const viewer = preview.previewImage(this.imagePreviewList)
      if (initialViewIndex >= 0) {
        viewer.view(initialViewIndex)
      }
    },
    addFile({ name, fk }) {
      this.$emit('change', this.singleFile ? [{ name, fk }] : [...this.fileList, { name, fk }])
    },
    deleteFile(file) {
      this.$emit(
        'change',
        this.fileList.filter(({ fk }) => fk !== file.fk)
      )
    },
    onChange(file) {
      const { name, size } = file
      let ext = name.substring(name.lastIndexOf('.') + 1)
      if (this.getAccept) {
        if (ext === '' || !this.getAccept.includes(ext)) {
          return this.$message.warning(`上传文件格式只支持${this.getAcceptDesc}`)
        }
      }
      if (size > this.maxSize) {
        this.$message.warning(`上传文件 ${name} 超出大小限制`)
        return
      }
      if (this.singleFile) {
        this.deleteFile(this.fileList[0])
        if (this.uploadingQueue.length) {
          return this.$message.warning(`文件上传中`)
        }
      }
      // 上传事件，默认设置上传进度0，增加监听
      this.$set(file, 'progress', 0)
      file.progress = 0
      let onProgress = ({ loaded, total }) => {
        const progress = Math.round((loaded / total) * 100)
        file.progress = progress
      }
      let upload = async () => {
        try {
          const fk = await uploadFile(file, {
            extraParam: this.extraParam,
            isPrivate: true,
            onProgress
          })
          // 设置新的文件列表
          this.addFile({ name, fk })
        } catch (e) {
          // 文件上传失败
          console.log(e)
          this.$message.error(`上传文件 ${name} 失败`)
        } finally {
          // 请求结束移除队列
          this.uploadingQueue = this.uploadingQueue.filter(f => f !== file)
          let canUploadCount = maxQueueCount - this.uploadingQueue.length
          if (this.waitingQueue.length) {
            const files = this.waitingQueue.splice(0, canUploadCount)
            files.forEach(file => this.onChange(file))
          }
        }
        // 上传完成后，从正在上传队列中注销，并从待上传队列取、执行
      }
      // 如果小于队列的限制，直接上传
      if (this.uploadingQueue.length < maxQueueCount) {
        this.uploadingQueue.push(file)
        upload()
      } else {
        this.waitingQueue.push(file)
      }
    },
    onClickDel(file) {
      let { name } = file
      this.$confirm(`确定移除 ${name === '' ? '该文件' : name}？`).then(() => {
        this.deleteFile(file)
      })
    },
    onClickDownload(href) {
      window.open(href)
    },
    handleExceed() {
      this.$message.warning('上传的文件数量超出限制')
    }
  }
}
</script>

<style lang="less">
@import '~@/common/styles/vars.less';
.zg-upload {
  .relative-view {
    position: relative;
  }
  .el-upload {
    width: 100%;
    .el-upload-dragger {
      width: 100%;
      background: @color-fc;
      height: auto;
      border-radius: 2px;
      border: 2px dashed #dcdcdc;
      padding: 20px 0;
      &:focus {
        outline: none;
      }
      &.is-dragover {
        border-color: #b4b4b4;
      }
    }
    &:focus {
      color: @color-3;
    }
    .file-list {
      flex-wrap: wrap;
    }
    .file--multiple {
      .mask {
        .action-bar {
          .el-image {
            width: 100%;
            height: 20px;
            line-height: 16px;
            text-align: center;
            .el-image__inner {
              width: 14px;
              height: 14px;
              vertical-align: middle;
            }
          }
        }
      }
    }
  }
  .two-line {
    line-height: 1.2;
  }
}
</style>

<style lang="less" scoped>
@import '~@/common/styles/vars.less';
.icon-upload {
  &--mini {
    height: 20px;
    vertical-align: sub;
  }
}
.tip {
  line-height: 22px;
}
.file {
  &--single {
    height: 150px;
    .el-image {
      width: 160px;
      height: 100px;
    }
    .name {
      width: 160px;
      margin: 10px auto 0;
    }
  }
  &--multiple {
    height: 140px;
    .el-image {
      width: 80px;
      height: 80px;
    }
    .mask {
      &:hover {
        opacity: 1;
      }
      opacity: 0;
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      .icon--del {
        position: absolute;
        top: 6px;
        left: 74px;
        color: rgba(51, 51, 51, 0.7);
        width: 14px;
        height: 14px;
      }
      .action-bar {
        background: rgba(51, 51, 51, 0.7);
        height: 20px;
        line-height: 20px;
        position: absolute;
        left: calc(50% - 40px);
        right: calc(50% - 40px);
        top: 61px;
        // .el-image {
        //   // padding: 2px 4px;
        //   width: 100%;
        //   height: 100%;
        //   .el-image__inner {
        //     width: 12px;
        //     height: 12px;
        //   }
        // }
      }
    }
  }
  &--others {
    .suffix {
      font-size: 12px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-weight: 600;
    }
  }
}
.img-border {
  border: 1px solid @color-f0;
}
</style>
